/**
 * Ocean Theme
 *
 * Cool, calming teal and blue tones.
 */

import type { AppTheme } from './types';

export const oceanTheme: AppTheme = {
  name: 'ocean',
  displayName: 'Ocean',
  description: 'Cool, calming blue and teal tones',

  antdTheme: {
    token: {
      colorPrimary: '#0891b2',
      colorSuccess: '#14b8a6',
      colorWarning: '#f59e0b',
      colorError: '#ef4444',
      colorInfo: '#0ea5e9',
      colorLink: '#0891b2',

      fontSize: 14,
      fontSizeHeading1: 28,
      fontSizeHeading2: 22,
      fontSizeHeading3: 18,
      fontSizeHeading4: 16,
      fontSizeHeading5: 14,
      fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      fontWeightStrong: 600,

      padding: 16,
      paddingLG: 24,
      paddingXL: 32,
      margin: 16,
      marginLG: 24,
      marginXL: 32,

      borderRadius: 8,
      borderRadiusLG: 10,
      borderRadiusSM: 6,
      borderRadiusXS: 4,

      colorBgContainer: '#ffffff',
      colorBgElevated: '#ffffff',
      colorBgLayout: '#f0f9ff',
      colorBorder: '#bae6fd',
      colorBorderSecondary: '#e0f2fe',

      colorText: '#0c4a6e',
      colorTextSecondary: '#0369a1',
      colorTextTertiary: '#7dd3fc',
      colorTextQuaternary: '#bae6fd',

      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04)',
      boxShadowSecondary: '0 1px 2px rgba(0, 0, 0, 0.03)',
    },

    components: {
      Layout: {
        headerBg: '#0891b2',
        headerColor: '#ffffff',
        headerHeight: 48,
        headerPadding: '0 20px',
        siderBg: '#0c4a6e',
        bodyBg: '#f0f9ff',
      },

      Button: {
        borderRadius: 8,
        borderRadiusLG: 10,
        borderRadiusSM: 6,
        controlHeight: 36,
        controlHeightLG: 42,
        controlHeightSM: 28,
        fontWeight: 500,
        primaryShadow: 'none',
      },

      Card: {
        borderRadius: 10,
        borderRadiusLG: 12,
        colorBgContainer: '#ffffff',
        boxShadowTertiary: 'none',
        padding: 20,
        paddingLG: 24,
      },

      Table: {
        headerBg: '#f0f9ff',
        headerColor: '#7dd3fc',
        headerBorderRadius: 8,
        borderRadius: 8,
        rowHoverBg: '#f0f9ff',
        cellPaddingBlock: 12,
        cellPaddingInline: 16,
      },

      Input: {
        borderRadius: 8,
        controlHeight: 36,
        controlHeightLG: 42,
      },

      Select: {
        borderRadius: 8,
        controlHeight: 36,
        controlHeightLG: 42,
      },

      Tag: {
        borderRadiusSM: 4,
        fontSizeSM: 12,
      },

      Modal: {
        borderRadiusLG: 12,
        boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08)',
      },

      Drawer: {
        colorBgElevated: '#ffffff',
      },

      Badge: {
        fontSize: 12,
      },

      Breadcrumb: {
        fontSize: 13,
        itemColor: '#7dd3fc',
        linkColor: '#0891b2',
      },

      Segmented: {
        borderRadius: 8,
        itemSelectedBg: '#0891b2',
        itemSelectedColor: '#ffffff',
      },

      Tabs: {
        itemColor: '#7dd3fc',
        itemSelectedColor: '#0c4a6e',
        itemHoverColor: '#0369a1',
      },

      Dropdown: {
        borderRadiusLG: 8,
      },

      Tooltip: {
        borderRadius: 6,
      },

      Divider: {
        colorSplit: '#e0f2fe',
      },
    },
  },

  cssVariables: {
    '--layout-header-bg': '#0891b2',
    '--layout-sidebar-bg': '#0c4a6e',
    '--layout-content-bg': '#f0f9ff',

    '--surface-0': '#f0f9ff',
    '--surface-1': '#ffffff',
    '--surface-2': '#ffffff',
    '--surface-3': '#ffffff',

    '--text-primary': '#0c4a6e',
    '--text-secondary': '#0369a1',
    '--text-tertiary': '#7dd3fc',
    '--text-muted': '#bae6fd',

    '--border-default': '#bae6fd',
    '--border-subtle': '#e0f2fe',
    '--border-strong': '#7dd3fc',

    '--border-color': '#bae6fd',
    '--border-radius': '8px',

    '--shadow-sm': '0 1px 2px rgba(0, 0, 0, 0.03)',
    '--shadow-md': '0 1px 3px rgba(0, 0, 0, 0.04)',
    '--shadow-lg': '0 4px 16px rgba(0, 0, 0, 0.08)',

    '--accent-warm': '#f59e0b',
    '--accent-cool': '#0ea5e9',

    '--status-success': '#14b8a6',
    '--status-warning': '#f59e0b',
    '--status-error': '#ef4444',
    '--status-info': '#0ea5e9',

    '--status-success-subtle': '#ecfdf5',
    '--status-warning-subtle': '#fffbeb',
    '--status-error-subtle': '#fef2f2',
    '--status-info-subtle': '#eff6ff',

    '--control-bg': '#f0f9ff',
    '--control-border': '#bae6fd',
    '--control-hover': '#e0f2fe',

    '--hover-bg': '#f0f9ff',
    '--active-bg': '#e0f2fe',
  },
};
